import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { requestPasswordReset, confirmPasswordReset } from "../lib/auth";

type Stage = "request" | "confirm" | "done";

/**
 * Self-service password reset.
 *
 * There was none at all: a customer who forgot their password had no way back into a paid account, and
 * nothing in the app even hinted at one. Cognito has supported this the whole time; only the screen was
 * missing.
 *
 * Two stages on one page rather than two routes, because the code is emailed to the address typed in the
 * first stage -- splitting them would mean either passing the address through the URL or asking for it
 * twice.
 */
export function ForgotPassword() {
  const [stage, setStage] = useState<Stage>("request");
  const [username, setUsername] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  async function handleRequest(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await requestPasswordReset(username.trim());
      setStage("confirm");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't start the reset. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirm(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await confirmPasswordReset(username.trim(), code.trim(), newPassword);
      setStage("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't reset the password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (stage === "done") {
    return (
      <div className="centered-page">
        <div className="card">
          <h1>Password changed</h1>
          <p className="plan-summary">You can sign in with your new password now.</p>
          <button type="button" onClick={() => navigate("/signin")}>
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="centered-page">
      {stage === "request" ? (
        <form className="card" onSubmit={handleRequest}>
          <h1>Reset your password</h1>
          <p className="plan-summary">
            Enter the email address on your account and we&rsquo;ll send you a verification code.
          </p>
          <label htmlFor="reset-username">Email</label>
          <input
            id="reset-username"
            type="email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
          />
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}
          <button type="submit" disabled={submitting}>
            {submitting ? "Sending…" : "Send code"}
          </button>
          <Link className="link-button" to="/signin">
            Back to sign in
          </Link>
        </form>
      ) : (
        <form className="card" onSubmit={handleConfirm}>
          <h1>Enter your code</h1>
          {/* Deliberately vague about whether an account exists -- requestPasswordReset resolves the
              same way either way, so this wording has to hold in both cases. */}
          <p className="plan-summary">
            If there&rsquo;s an account for {username}, we&rsquo;ve sent it a verification code. Enter it below with
            your new password.
          </p>
          <label htmlFor="reset-code">Verification code</label>
          <input
            id="reset-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoComplete="one-time-code"
            inputMode="numeric"
            required
          />
          <label htmlFor="reset-password">New password</label>
          <input
            id="reset-password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}
          <button type="submit" disabled={submitting}>
            {submitting ? "Saving…" : "Set new password"}
          </button>
          <button type="button" className="chip" onClick={() => setStage("request")}>
            Use a different email
          </button>
        </form>
      )}
    </div>
  );
}
